import { createFileRoute, Navigate } from "@tanstack/react-router";
import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Subheading } from "src/components/base/heading";
import SINGLE_USER_CONTEXT from "src/context/internal/user";
import { Text } from "src/components/base/text";
import { DescriptionDetails, DescriptionList, DescriptionTerm } from "src/components/base/description-list";
import { Field, FieldGroup, Fieldset } from "src/components/base/fieldset";
import { Button } from "src/components/base/button";
import { notify } from "src/components/base/toast";
import { CheckIcon, ClipboardIcon, ClockIcon } from "@heroicons/react/20/solid";
import { UUID } from "src/api/api_common";
import { Badge } from "src/components/base/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { ApiInternal } from "src/api/api_internal";
import RecreateInviteDialog from "src/components/dialogs/recreate-invitation";
import { XMarkIcon } from "@heroicons/react/16/solid";

/**
 * The properties for {@link InvitationUserView}
 */
export type InvitationUserViewProps = {};

/**
 * The view for invitation
 */
export default function InvitationUserView(props: InvitationUserViewProps) {
    const [t] = useTranslation("admin-user-invitation");
    const [tg] = useTranslation();

    const { public_origin } = Route.useLoaderData();
    const [recreateInvitation, setRecreateInvitation] = React.useState(false);

    const { user, reset } = React.useContext(SINGLE_USER_CONTEXT);

    if (user.auth_specific.type !== "Invited") {
        return <Navigate to={"/"} />;
    }
    const data = user.auth_specific;

    /**
     * Revoke the invite of the user
     */
    const revokeInvite = async () => {
        await ApiInternal.users.expireInvitation(user.uuid);
        notify.success(t("toast.user-invite-revoked"));
        reset();
    };

    /**
     * Formats the invitation link
     *
     * @param linkUuid The UUID of the link
     * @returns The formatted link
     */
    const formatInvitationLink = (linkUuid: UUID) => new URL("/s/invite/" + linkUuid, public_origin).href;

    return (
        <div className={"flex flex-col gap-6"}>
            <Subheading>{t("heading.current-invitation")}</Subheading>

            <DescriptionList>
                <DescriptionTerm>{t("label.invitation-link")}</DescriptionTerm>
                <DescriptionDetails>
                    {data.current_link ? (
                        <Fieldset>
                            <FieldGroup>
                                <Field>
                                    <div className={"flex items-center gap-2"}>
                                        <Text>{formatInvitationLink(data.current_link.uuid)}</Text>
                                        <Button
                                            plain={true}
                                            disabled={!data.current_link}
                                            onClick={async () => {
                                                if (!data.current_link) return;

                                                await navigator.clipboard.writeText(
                                                    formatInvitationLink(data.current_link.uuid),
                                                );
                                                notify.success(tg("toast.copied-to-clipboard"));
                                            }}
                                        >
                                            <ClipboardIcon />
                                        </Button>
                                    </div>
                                </Field>
                            </FieldGroup>
                        </Fieldset>
                    ) : (
                        <Text>-</Text>
                    )}
                </DescriptionDetails>
                <DescriptionTerm>{t("label.created-at")}</DescriptionTerm>
                <DescriptionDetails>
                    <Text>{data.current_link ? new Date(data.current_link.created_at).toLocaleString() : "-"}</Text>
                </DescriptionDetails>
                <DescriptionTerm>{t("label.invitation-expires-at")}</DescriptionTerm>
                <DescriptionDetails>
                    {data.current_link && data.current_link.expires_at ? (
                        <div className={"flex gap-4"}>
                            <Text>{new Date(data.current_link.expires_at).toLocaleString()}</Text>
                            {new Date(data.current_link.expires_at).getTime() < Date.now() ? (
                                <Badge color={"red"}>
                                    <XMarkIcon className={"size-4"} />
                                    {t("label.expired")}
                                </Badge>
                            ) : (
                                <Badge color={"green"}>
                                    <CheckIcon className={"size-4"} />
                                    {t("label.valid")}
                                </Badge>
                            )}
                        </div>
                    ) : (
                        <Text>-</Text>
                    )}
                </DescriptionDetails>
                {data.is_access_planned ? (
                    <>
                        <DescriptionTerm>{t("label.access-period")}</DescriptionTerm>
                        <DescriptionDetails>
                            {data.access_expires_at && data.access_start ? (
                                <div className={"flex gap-4"}>
                                    <Text>
                                        {new Date(data.access_start).toLocaleString() +
                                            " - " +
                                            new Date(data.access_expires_at).toLocaleString()}
                                    </Text>
                                    <Badge color={"blue"}>
                                        <ClockIcon className={"size-4"} />
                                        {t("label.access-planned")}
                                    </Badge>
                                </div>
                            ) : (
                                <Text>-</Text>
                            )}
                        </DescriptionDetails>
                    </>
                ) : (
                    <>
                        <DescriptionTerm>{t("label.access-will-expire")}</DescriptionTerm>
                        <DescriptionDetails>
                            {data.access_expires_at ? (
                                <div className={"flex gap-4"}>
                                    <Text>{new Date(data.access_expires_at).toLocaleString()}</Text>
                                    {new Date(data.access_expires_at).getTime() < Date.now() ? (
                                        <Badge color={"red"}>
                                            <XMarkIcon className={"size-4"} />
                                            {t("label.expired")}
                                        </Badge>
                                    ) : (
                                        <Badge color={"green"}>
                                            <CheckIcon className={"size-4"} />
                                            {t("label.valid")}
                                        </Badge>
                                    )}
                                </div>
                            ) : (
                                <Text>-</Text>
                            )}
                        </DescriptionDetails>
                    </>
                )}
            </DescriptionList>

            <div className={"inline-block"}>
                {data.current_link ? (
                    <Button onClick={revokeInvite}>{t("button.revoke-invite")}</Button>
                ) : (
                    <Button onClick={() => setRecreateInvitation(true)}>{t("button.create-new-invite")}</Button>
                )}
            </div>

            <div className={"mt-4"}>
                <Table dense={true} className={"mt-3"}>
                    <TableHead>
                        <TableRow>
                            <TableHeader>{t("label.previous-links")}</TableHeader>
                            <TableHeader>{t("label.invitation-created-at")}</TableHeader>
                            <TableHeader>{t("label.invitation-expires-at")}</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.prev_links.map((link, index) => (
                            <TableRow key={link.uuid}>
                                <TableCell>{t("label.previous-link", { object: index })}</TableCell>
                                <TableCell>{new Date(link.created_at).toLocaleString()}</TableCell>
                                <TableCell>{new Date(link.expires_at).toLocaleString()}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Suspense>
                <RecreateInviteDialog
                    user={user.uuid}
                    isOpen={recreateInvitation}
                    onSubmit={() => {
                        setRecreateInvitation(false);
                        notify.success(t("toast.user-invite-created"));
                        reset();
                    }}
                    onClose={() => setRecreateInvitation(false)}
                />
            </Suspense>
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/users/$userId/_user/invitation")({
    component: InvitationUserView,
    loader: () => ApiInternal.settings.getGeneral(),
});
